// Third party
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import $http from './http.service';
import Endpoints from '@/constants/endpoints';

const nifDigitControls = ['J', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];
const dniNieControlDigits = [
  'T',
  'R',
  'W',
  'A',
  'G',
  'M',
  'Y',
  'F',
  'P',
  'D',
  'X',
  'B',
  'N',
  'J',
  'Z',
  'S',
  'Q',
  'V',
  'H',
  'L',
  'C',
  'K',
  'E',
];
const nieFirstLetters = {
  X: 0,
  Y: 1,
  Z: 2,
};

export default class UtilsService {
  public static inDebug() {
    return process.env.VUE_APP_DEBUG === 'true';
  }

  public static easeInOutQuad(
    currentTime: number,
    start: number,
    change: number,
    duration: number,
  ) {
    currentTime /= duration / 2;
    if (currentTime < 1) {
      return (change / 2) * currentTime * currentTime + start;
    }
    currentTime--;
    return (-change / 2) * (currentTime * (currentTime - 2) - 1) + start;
  }

  public static imageExists(imageUrl: string) {
    return new Promise((resolve) => {
      const imageObj = new Image();
      imageObj.onload = () => resolve(true);
      imageObj.onerror = () => resolve(false);
      imageObj.src = imageUrl;
    });
  }

  public static getQueryParams(payload: any) {
    let params: any = {};
    if (payload === undefined) {
      return params;
    }

    let queryParams = payload.queryParams;
    if (queryParams === undefined) {
      queryParams = payload;
    }
    for (const key in queryParams) {
      if (queryParams.hasOwnProperty(key)) {
        const value = queryParams[key];
        if (value) {
          const newKey = key
            .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
            .toLowerCase();
          params = Object.assign({}, params, { [newKey]: value });
        }
      }
    }
    return params;
  }

  public static getQueryParamsFormatted(payload: any) {
    let queryParams: string = '';

    for (const key in payload) {
      if (payload.hasOwnProperty(key)) {
        const value = payload[key];
        if (value) {
          const newKey = key
            .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
            .toLowerCase();
          queryParams += `&${newKey}=${value}`;
        }
      }
    }
    queryParams = queryParams.replace('&', '?');

    return queryParams;
  }

  public static isLetter(str: string) {
    return str.length === 1 && str.match(/[a-zA-Z]/i);
  }

  public static isNumber(str: string) {
    return str.length === 1 && str.match(/[0-9]/i);
  }

  private static padLeft(val: number, width: number, paddler?: any) {
    paddler = paddler || '0';
    const value = val + '';
    return value.length >= width
      ? value
      : new Array(width - value.length + 1).join(paddler) + value;
  }

  public static checkNif(nif: string) {
    if (nif.length > 8) {
      return false;
    } else {
      const nifLength = nif.length;
      let nifNumber: number | string = parseInt(
        nif.substr(0, nifLength - 1),
        10,
      );
      if (isNaN(nifNumber)) {
        return false;
      } else {
        nifNumber = this.padLeft(nifNumber, 7);
        const nifLetter = nif.substr(nifLength - 1, 1);
        let evenSum = 0;
        let oddSum = 0;
        for (let i = 0; i <= 6; i++) {
          if (i % 2 === 0) {
            const valueDoubled = 2 * parseInt(nifNumber.substr(i, 1), 10);
            let valueToSum;
            if (valueDoubled > 9) {
              valueToSum = 1 + valueDoubled - 10;
            } else {
              valueToSum = valueDoubled;
            }
            oddSum += parseInt(valueToSum + '', 10);
          } else {
            evenSum += parseInt(nifNumber.substr(i, 1), 10);
          }
        }
        const sum = evenSum + oddSum;
        const lastDigitSum = sum % 10;
        const index = lastDigitSum === 0 ? 0 : 10 - lastDigitSum;
        return (
          nifLetter.toLowerCase() === nifDigitControls[index].toLowerCase()
        );
      }
    }
  }

  public static checkDniNie(dniNie: string) {
    if (dniNie.length > 9) {
      return false;
    } else {
      const dniNieLength = dniNie.length;
      const dniNieNumber = parseInt(dniNie.substr(0, dniNieLength - 1), 10);
      if (isNaN(dniNieNumber)) {
        return false;
      } else {
        const dniNieLetter = dniNie.substr(dniNieLength - 1, 1);
        const modDniNieNumber = dniNieNumber % 23;
        return (
          dniNieLetter.toLowerCase() ===
          dniNieControlDigits[modDniNieNumber].toLowerCase()
        );
      }
    }
  }

  public static validateNie(nie: string) {
    const firstCharacter = nie.substr(0, 1);
    if (
      firstCharacter === 'X' ||
      firstCharacter === 'Y' ||
      firstCharacter === 'Z'
    ) {
      const valueLength = nie.length;
      const nieToCheck =
        nieFirstLetters[firstCharacter] + nie.substr(1, valueLength - 1);
      return this.checkDniNie(nieToCheck);
    } else {
      return false;
    }
  }

  public static validateDni(dni: string) {
    const firstCharacter = dni.substr(0, 1);
    if (isNaN(parseInt(firstCharacter, 10))) {
      if (
        firstCharacter === 'K' ||
        firstCharacter === 'L' ||
        firstCharacter === 'M'
      ) {
        const valueLength = dni.length;
        const nifToCheck = dni.substr(1, valueLength - 1);
        return this.checkNif(nifToCheck);
      } else {
        return false;
      }
    } else {
      return this.checkDniNie(dni);
    }
  }

  public static setNifMask(nif: string, currentMask: string) {
    let nifMask = currentMask;
    if (nif !== null && nif !== undefined) {
      if (nif.length === 0) {
        nifMask = 'XX######XX';
      } else if (nif.length > 0 && UtilsService.isLetter(nif)) {
        nifMask = 'AX#######';
      } else if (
        nif.length > 1 &&
        UtilsService.isLetter(nif[0]) &&
        UtilsService.isLetter(nif[1])
      ) {
        nifMask = 'AAA######A';
      } else if (
        UtilsService.isLetter(nif) ||
        (nif.length === 8 && UtilsService.isLetter(nif[0]))
      ) {
        nifMask = 'A#######X';
      } else if (UtilsService.isNumber(nif)) {
        nifMask = '########A';
      } else if (
        nif.length === 9 &&
        UtilsService.isLetter(nif[8]) &&
        !UtilsService.isNumber(nif[0])
      ) {
        nifMask = 'A#######A';
      } else if (
        nif.length === 9 &&
        UtilsService.isNumber(nif[8]) &&
        !UtilsService.isNumber(nif[0])
      ) {
        nifMask = 'A########A';
      }
    }
    return nifMask;
  }

  public static setDniMask(dni: string) {
    return ['K#######A', 'L#######A', 'M#######A', '########A'];
  }

  public static setPassportMask(dni: string) {
    return ['AAA######', 'AAA######'];
  }

  public static setTimeToCero(date: any) {
    return date
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0);
  }

  public static setTimeToLast(date: any) {
    return date
      .set('hour', 23)
      .set('minute', 59)
      .set('second', 59)
      .set('millisecond', 59);
  }

  public static objectIsEmpty(obj: any) {
    return (
      obj && Object.entries(obj).length === 0 && obj.constructor === Object
    );
  }

  public static fieldValue(item: any, field: any) {
    const levels = field.split('.');
    let itemValue = item;
    levels.forEach((level: any) => {
      itemValue = itemValue[level];
    });

    return itemValue;
  }

  public static parseToObject(optionFields: any, list: any[]) {
    const objectList: any[] = [];
    let items = list;
    if (!(list instanceof Array) && list !== null) {
      items = [list];
    }

    if (
      !this.objectIsEmpty(optionFields) &&
      items !== null &&
      items.length > 0
    ) {
      const keys = Object.keys(optionFields);
      items.forEach((item: any) => {
        const itemObject: any = {};
        keys.forEach((key: string) => {
          itemObject[key] = this.fieldValue(item, optionFields[key]);
        });

        objectList.push(itemObject);
      });
    }

    return objectList;
  }

  public static checkValidEmail(email: string) {
    // tslint:disable-next-line:max-line-length
    const pattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return pattern.test(email.toLowerCase().trim());
  }

  public static getCookie(name: string) {
    const match = document.cookie.match(new RegExp(name + '=([^;]+)'));
    if (match) {
      return match[1];
    }
    return;
  }

  public static setCookie(
    name: string,
    value: string,
    daysToExpire: number = 30,
  ): void {
    const date = new Date();
    date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value}; expires=${date.toUTCString()}`;
  }

  public static unformattedToday() {
    return dayjs()
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0);
  }

  public static today() {
    return this.unformattedToday().format('YYYY-MM-DD');
  }

  public static yesterday() {
    return this.unformattedToday()
      .subtract(1, 'day')
      .format('YYYY-MM-DD');
  }

  public static isEquivalent(a: any, b: any) {
    const aProps = Object.getOwnPropertyNames(a);
    const bProps = Object.getOwnPropertyNames(b);

    if (aProps.length !== bProps.length) {
      return false;
    }

    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < aProps.length; i++) {
      const propName = aProps[i];

      if (a[propName] !== b[propName]) {
        return false;
      }
    }

    return true;
  }

  public static rejectBudget(budgetId: string) {
    return $http.get(Endpoints.user_permissions);
  }
}
